import React, { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { useBFFCartServiceApi } from "@api/hooks/cartService.hook";

import { useIsClientSide } from "@utils/hooks/isClient.hook";

import CallService, { CallServiceKey } from "@services/call.service";

import { useThunkDispatch } from "@redux/dispatcher";
import { CartItemModel } from "@redux/modules/api/data/cart/@types/cart.model";
import * as CartModule from "@redux/modules/api/data/cart/cart.module";
import * as CartSelectors from "@redux/modules/api/data/cart/cart.selectors";
import * as HeaderSelectors from "@redux/modules/value/header/header.selectors";

import { MiniCart } from "@components/CartButton/miniCart/miniCart.component";
import {
    HeaderButton,
    HeaderButtonProps,
} from "@components/General/HeaderButton/headerButton.component";

export type CartButtonContainerProps = HeaderButtonProps & {
    redirect?: string;
    innerHTML: any;
};

const cartItemsCount = (items: CartItemModel[]) => {
    if (items.length === 0) {
        return 0;
    }
    let count = items
        .filter(i => !i.isAdditionalService)
        .reduce((acc, item) => {
            acc += item.quantity;
            return acc;
        }, 0);

    return count < 100 ? count : "+99";
};

const CartButtonContainer = (props: CartButtonContainerProps) => {
    const { invert, text, redirect, link, large } = props;

    const isClientSide = useIsClientSide();
    const [isUpdatingCart, setIsUpdatingCart] = useState({});
    const [activeId, setActiveId] = useState("");
    const cart = useSelector(CartSelectors.getCart);
    const isLoadingCart = useSelector(CartSelectors.isLoadingCart);
    const header = useSelector(HeaderSelectors.getHeader);
    const dispatch = useThunkDispatch();

    const [{ data }, loadBadge] = useBFFCartServiceApi(
        {
            url: "/carts/items/count",
            method: "GET",
        },
        {
            manual: true,
        },
    );

    const shouldCall =
        isClientSide &&
        CallService.shouldCall(CallServiceKey.Cart, {
            days: 30,
        });

    useEffect(() => {
        if (shouldCall && !isLoadingCart) {
            loadBadge().then(
                payload => {
                    if (payload.data > 0) {
                        CallService.enable(CallServiceKey.Cart);
                    } else {
                        CallService.disable(CallServiceKey.Cart);
                    }
                },
                () => {
                    CallService.enable(CallServiceKey.Cart);
                },
            );
        }
    }, [isClientSide, shouldCall, isLoadingCart]);

    const doRemoveFromCart = (id: string, quantity: number, free: boolean) => {
        setActiveId(id);
        setIsUpdatingCartForId(id, true);
        dispatch(CartModule.removeFromCart(id, quantity, free)).then(() =>
            setIsUpdatingCartForId(id, false),
        );
    };

    const setIsUpdatingCartForId = (id: string, isUpdatingCart: boolean) => {
        setIsUpdatingCart((cur: any) => ({
            ...cur.isUpdatingCart,
            [id]: isUpdatingCart,
        }));
    };

    const componentShouldInvert = isClientSide
        ? invert || header?.mobile
        : false;

    const cartItems = cartItemsCount(cart?.items || []);
    const itemCount = isClientSide ? (!!cart?.items ? cartItems : data) : 0;

    return (
        <HeaderButton
            {...props}
            invert={componentShouldInvert}
            text={text}
            hover={true}
            addCloseCallback={true}
            large={large}
            link={
                isClientSide && (cart?.items?.length || 0) > 0 ? redirect : link
            }
            alerts={itemCount}
        >
            {isClientSide ? (
                <MiniCart
                    removeFromCart={doRemoveFromCart}
                    urlTarget={redirect}
                    shoppingCart={cart}
                    isUpdatingCart={isUpdatingCart}
                    activeId={activeId}
                />
            ) : (
                <Fragment />
            )}
        </HeaderButton>
    );
};

export { CartButtonContainer };
